import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Button, EmptyState, Input, Modal, NoticeBox, Select, Tags } from "../ui";
import { categoryLabel } from "../copilot/copilotText";
import {
  archiveSavedReplyAPI,
  createSavedReplyAPI,
  listSavedRepliesAPI,
  markSavedReplyUsedAPI,
  updateSavedReplyAPI,
} from "../../utils/api/copilotAdmin";
import { adminErrorMessage } from "./copilotAdminText";
import { useConfirmedAction } from "./useConfirmedAction";

// {{customer_name}}, {{reference}}, {{agent_name}} are filled in when a reply is inserted.
export const fillPlaceholders = (body, values) =>
  String(body).replace(/\{\{\s*(customer_name|reference|agent_name)\s*\}\}/g, (_, key) => values[key] || "");

const BLANK = { title: "", body: "", lang: "any", category: "" };

// Picker (every agent) and editor (managers) for saved replies. Rendered into
// document.body like the other inbox dialogs.
export default function SavedRepliesDialog({ t, lang, categories, conversation, agentName, canManage, onInsert, onClose }) {
  const confirmation = useConfirmedAction(lang);
  const titleId = useId();
  const [state, setState] = useState({ status: "loading", items: [], error: null });
  const [search, setSearch] = useState("");
  const [language, setLanguage] = useState(lang === "ar" ? "ar" : "en");
  const [form, setForm] = useState(null); // null | { id?, ...fields }
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    const mine = (ticket.current += 1);
    setState((prev) => ({ ...prev, status: prev.items.length ? "ready" : "loading", error: null }));
    try {
      const params = { search: search.trim() || undefined, lang: language === "all" ? undefined : language };
      const data = await listSavedRepliesAPI(params);
      if (mine === ticket.current) setState({ status: "ready", items: data.replies, error: null });
    } catch (error) {
      if (mine === ticket.current) setState((prev) => ({ ...prev, status: prev.items.length ? "ready" : "error", error: adminErrorMessage(error, t) }));
    }
  }, [search, language, t]);

  // Search is debounced; other changes load at once.
  useEffect(() => {
    const timer = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const values = { customer_name: conversation?.customer?.name || "", reference: conversation?.reference || "", agent_name: agentName || "" };

  const insert = (reply) => confirmation.ask(async () => {
    await markSavedReplyUsedAPI(reply.id).catch(() => {}); // Usage tracking must not block drafting.
    onInsert(fillPlaceholders(reply.body, values));
    onClose();
  }, "insert", reply.title);

  const validate = () => {
    const found = {};
    if (form.title.trim().length < 2) found.title = t.savedReplies.titleRequired;
    if (!form.body.trim()) found.body = t.savedReplies.bodyRequired;
    else if (form.body.length > 5000) found.body = t.savedReplies.bodyLong;
    return found;
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;
    confirmation.ask(async () => {
      setSaving(true);
      try {
        const payload = { title: form.title.trim(), body: form.body.trim(), lang: form.lang, category: form.category };
        if (form.id) await updateSavedReplyAPI(form.id, payload);
        else await createSavedReplyAPI(payload);
        setForm(null);
        setNotice({ type: "success", text: t.savedReplies.saved });
        load();
      } catch (error) {
        setNotice({ type: "error", text: adminErrorMessage(error, t) });
        return false;
      } finally {
        setSaving(false);
      }
    }, form.id ? "save" : "create", form.title);
  };

  const archive = (reply) => confirmation.ask(async () => {
    try {
      await archiveSavedReplyAPI(reply.id);
      setNotice({ type: "success", text: t.savedReplies.archived });
      load();
    } catch (error) {
      setNotice({ type: "error", text: adminErrorMessage(error, t) });
      return false;
    }
  }, "archive", reply.title, true);

  const langOptions = [
    { value: "any", label: t.savedReplies.langAny },
    { value: "ar", label: "العربية" },
    { value: "en", label: "English" },
  ];
  const filterLangOptions = [{ value: "all", label: t.savedReplies.langAll }, ...langOptions.slice(1)];
  const categoryOptions = [{ value: "", label: t.allCategories }, ...(categories || []).map((cat) => ({ value: cat.value, label: lang === "ar" ? cat.labelAr : cat.labelEn }))];

  const footer = form ? (
    <>
      <Button variant="neutral" appearance="outline" disabled={saving} onClick={() => setForm(null)}>{t.cancel}</Button>
      <Button type="submit" form={`${titleId}-form`} variant="primary" appearance="filled" loading={saving}>{t.save}</Button>
    </>
  ) : (
    <Button variant="neutral" appearance="outline" onClick={onClose}>{t.close}</Button>
  );

  return (
    <><Modal size="lg" title={t.savedReplies.title} onClose={() => (form ? setForm(null) : onClose())} busy={saving} footer={footer}>
      {notice && <NoticeBox variant={notice.type === "error" ? "danger" : "success"} size="sm">{notice.text}</NoticeBox>}

      {form ? (
        <form id={`${titleId}-form`} className="ca-reply-form" onSubmit={save} noValidate>
          <Input label={t.savedReplies.fieldTitle} value={form.title} maxLength={80} onChange={(e) => setForm({ ...form, title: e.target.value })} error={errors.title || false} autoFocus />
          <Input
            textarea
            rows={5}
            label={t.savedReplies.fieldBody}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            error={errors.body || false}
            helperText={t.savedReplies.placeholders}
          />
          <div className="ca-field-row">
            <Select label={t.savedReplies.fieldLang} options={langOptions} value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} />
            <Select label={t.category} options={categoryOptions} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </div>
        </form>
      ) : (
        <>
          <div className="ca-replies-tools">
            <Input
              type="search"
              iconStart={<Search size={16} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.savedReplies.search}
              aria-label={t.savedReplies.search}
              maxLength={60}
            />
            <Select label={t.savedReplies.fieldLang} options={filterLangOptions} value={language} onChange={(e) => setLanguage(e.target.value)} className="ca-replies-lang" />
            {canManage && (
              <Button variant="primary" appearance="filled" icon={<Plus size={14} />} onClick={() => { setForm({ ...BLANK, lang: language === "all" ? "any" : language }); setErrors({}); }}>
                {t.savedReplies.new}
              </Button>
            )}
          </div>
          <div className="ca-replies-list" aria-busy={state.status === "loading"}>
            {state.status === "loading" && <EmptyState variant="loading" compact>{t.loading}</EmptyState>}
            {state.status === "error" && (
              <EmptyState variant="error" compact action={<Button variant="neutral" appearance="outline" size="sm" onClick={load}>{t.retry}</Button>}>{state.error}</EmptyState>
            )}
            {state.status === "ready" && state.items.length === 0 && <EmptyState compact>{search ? t.savedReplies.noMatch : t.savedReplies.empty}</EmptyState>}
            <ul>
              {state.items.map((reply) => (
                <li key={reply.id} className="ca-reply">
                  <div className="ca-reply-main">
                    <strong>{reply.title}</strong>
                    <span className="ca-reply-tags">
                      {reply.lang !== "any" && <Tags size="sm" variant="primary">{reply.lang === "ar" ? "AR" : "EN"}</Tags>}
                      {reply.category && <Tags size="sm" variant="neutral">{categoryLabel(categories, reply.category, lang)}</Tags>}
                    </span>
                    <p className="ca-reply-body">{reply.body}</p>
                  </div>
                  <div className="ca-reply-actions">
                    <Button size="sm" variant="primary" appearance="soft" onClick={() => insert(reply)}>{t.savedReplies.insert}</Button>
                    {canManage && (
                      <>
                        <Button size="sm" variant="neutral" appearance="ghost" aria-label={`${t.savedReplies.edit}: ${reply.title}`} title={t.savedReplies.edit} icon={<Pencil size={14} />} onClick={() => { setForm({ id: reply.id, title: reply.title, body: reply.body, lang: reply.lang, category: reply.category }); setErrors({}); }} />
                        <Button size="sm" variant="danger" appearance="ghost" aria-label={`${t.savedReplies.archive}: ${reply.title}`} title={t.savedReplies.archive} icon={<Trash2 size={14} />} onClick={() => archive(reply)} />
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </Modal>{confirmation.dialog}</>
  );
}
